/**
 * Shared design-system types and class maps.
 *
 * Centralises primitives that are reused across multiple rendering components.
 * Import from here — never duplicate these maps in individual components.
 *
 * To extend:
 *   1. Add the new value to the union type (e.g. HeadingSize).
 *   2. Add the corresponding Tailwind class(es) to the map.
 *   3. If a new CSS utility is needed, define it in globals.css § DS UTILITY ALIASES.
 *   4. Update any editor panel <select> options that expose the new value.
 */

// ── Heading size ────────────────────────────────────────────
// Each key maps to a responsive mobile → desktop pair.
// Mobile is one step down to prevent oversized text on small screens.
export type HeadingSize = "sm" | "md" | "mdlg" | "lg";

export const HEADING_SIZE_CLASSES: Record<HeadingSize, string> = {
  sm: "text-heading-sm",
  md: "text-heading-sm md:text-heading-md",
  mdlg: "text-heading-md md:text-heading-mdlg",
  lg: "text-heading-mdlg md:text-heading-lg",
};

// ── Padding ─────────────────────────────────────────────────
export type SectionPadding = "default" | "large" | "xlarge";

// ── Content alignment ───────────────────────────────────────
// Values encode "{mobile}-{desktop}".
// The class map is breakpoint-agnostic — consumers provide the prefix.
export type ContentAlign =
  | "left-left"
  | "left-center"
  | "center-center"
  | "center-left";

/**
 * Build a breakpoint-specific class map for ContentAlign.
 * `bp` is the Tailwind breakpoint prefix (e.g. "md", "lg").
 */
export function contentAlignClasses(bp: string): Record<ContentAlign, string> {
  // Classes must be full static literals so Tailwind's scanner can detect them.
  const map: Record<string, Record<ContentAlign, string>> = {
    lg: {
      "left-left": "text-left lg:text-left",
      "left-center": "text-left lg:text-center",
      "center-center": "text-center lg:text-center",
      "center-left": "text-center lg:text-left",
    },
    md: {
      "left-left": "text-left md:text-left",
      "left-center": "text-left md:text-center",
      "center-center": "text-center md:text-center",
      "center-left": "text-center md:text-left",
    },
  };
  return map[bp] ?? map.lg;
}

/** Coerce legacy values ("left" | "center") to the combined format. */
export function normalizeContentAlign(
  value: ContentAlign | "left" | "center" | undefined,
): ContentAlign {
  if (value === undefined || value === "left") return "left-left";
  if (value === "center") return "center-center";
  return value;
}
